//@author: travis
import {
    ChangeDetectionStrategy,
    Component,
    computed,
    effect,
    ElementRef,
    inject,
    input,
    model,
    numberAttribute,
    output,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { FormValueControl } from "@angular/forms/signals";
import { Contact } from "../../../pojo/Contact";
import { DropDownItem } from "../../../pojo/DropDownItem";
import { Item } from "../../../pojo/Item";
import { Nullable } from "../../../pojo/Util";
import { OrganizationService } from "../../../services/organization.service";
import { SearchCriteriaService } from "../../../services/search/search-criteria/search.criteria.service";
import { AriaLabel } from "../../../util/aria/aria.label";
import { S25Util } from "../../../util/s25-util";
import { s25Resource } from "../../../util/s25.resource";
import { TypeManagerDecorator } from "../../../main/type.map.service";
import { DropdownDataUtil } from "../dropdown.data.util";
import { S25GenericDropdownComponent } from "../generic/s25.generic.dropdown.component";
import { S25DropdownPaginatedComponent } from "../s25.dropdown.paginated.component";

@TypeManagerDecorator("s25-contact-dropdown")
@Component({
    selector: "s25-contact-dropdown",
    template: `
        <s25-generic-dropdown
            [value]="value()"
            [hasFav]="true"
            [onSelect]="onSelect()"
            [items]="items()"
            [staticItems]="items()"
            [focusFirst]="false"
            [searchEnabled]="true"
            [serverSide]="serverSide()"
            [resetSelectedOnCleanup]="resetSelectedOnCleanup()"
            [placeholder]="placeholder()"
            [noSelection]="noSelection()"
            [ariaLabel]="ariaLabel()"
            [blockDetailsNav]="blockDetailsNav()"
            (valueChange)="onValueChange($event)"
        />
    `,
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class S25ContactDropdownComponent implements FormValueControl<Nullable<DropDownItem>> {
    // --- contact-only (both become query params on /contact/contacts.json) ---
    readonly usersOnly = input(false, { transform: S25Util.toBool });
    readonly usernameAsItemName = input(false, { transform: S25Util.toBool });
    readonly organizationId = input(undefined, { transform: numberAttribute });
    /** Event contact role being filled. Scheduler roles may only be assigned 25Live users. */
    readonly roleId = input(undefined, { transform: numberAttribute });

    // --- shared dropdown knobs (from abstract / common concrete API; we do not extend abstract) ---
    readonly value = model<Nullable<DropDownItem>>();
    readonly onSelect = input<(item: Nullable<DropDownItem>) => void>();
    readonly onLoad = input<() => void>();
    readonly staticItems = input<DropDownItem[]>([]);
    readonly ariaLabel = input<AriaLabel>();
    readonly blockDetailsNav = input(false);
    readonly resetSelectedOnCleanup = input(false);
    readonly noSelection = input<string | undefined>(undefined);
    readonly placeholder = input("Select a Contact");

    /** @deprecated angJS workaround; can't bind to `valueChange`, because it's a `model()`. */
    readonly contactSelected = output<Nullable<DropDownItem>>();

    private readonly elementRef = inject(ElementRef);
    readonly dropdown = viewChild(S25GenericDropdownComponent);

    private readonly restrictToUsers = computed(
        () => this.usersOnly() || this.roleId() === Contact.Role.Event.id.Scheduler,
    );

    readonly customFilterValue = computed(() =>
        [this.restrictToUsers() ? "&is_r25user=1" : "", this.usernameAsItemName() ? "&itemName=username" : ""].join(""),
    );

    readonly favoriteContacts = s25Resource({
        params: () => this.customFilterValue(),
        loader: ({ params: customFilterValue }): Promise<DropDownItem[]> =>
            SearchCriteriaService.getContacts(-999, 0, 999, null, true, null, customFilterValue).then(
                (data) => (data && data.searchCriteria && data.searchCriteria.item) || [],
            ),
    });

    readonly organizationContacts = s25Resource({
        if: () => Number.isInteger(this.organizationId()),
        params: () => this.organizationId(),
        loader: ({ params: organizationId }): Promise<DropDownItem[]> =>
            OrganizationService.getOrganizationContacts(organizationId).then((contacts) =>
                S25Util.array.forceArray(contacts).map((contact) => ({
                    grp: "Organization Contacts",
                    itemId: parseInt(String(contact.contact_id), 10),
                    itemTypeId: Item.Ids.Contact,
                    itemName: contact.contact_name + (contact.contact_role ? ` [${contact.contact_role}]` : ""),
                    itemDesc: contact.contact_email,
                })),
            ),
    });

    readonly items = computed(() => this.combineStaticWithFavoriteItems());

    readonly serverSide = computed(() =>
        DropdownDataUtil.getServerSideModel("getContacts", this.elementRef.nativeElement, this.customFilterValue()),
    );

    constructor() {
        effect(() => {
            if (this.value()) {
                this.value()!.itemTypeId = Item.Ids.Contact;
            }
        });

        const onLoadEffect = effect(() => {
            if (this.onLoad() && !this.favoriteContacts.isLoading()) {
                this.onLoad()!();
                onLoadEffect.destroy();
            }
        });

        effect(() => {
            const selected = this.value();
            this.items();
            if (selected?.itemId) {
                queueMicrotask(() => this.applyProgrammaticSelectionDisplay());
            }
        });
    }

    private combineStaticWithFavoriteItems(): DropDownItem[] {
        const favoriteContacts = this.favoriteContacts.value();
        const staticItems = [...this.staticItems(), ...(this.organizationContacts.value() ?? [])];
        const hasGroup = staticItems.some((item) => !!item.grp);
        const favoriteByItemId = new Map(
            favoriteContacts?.map((contact) => [parseInt(String(contact.itemId), 10), contact] as const),
        );

        const items = staticItems.map((item) => {
            if (favoriteByItemId.has(parseInt(String(item.itemId), 10))) {
                return { ...item, favorite: "T" as const };
            }
            return item;
        });

        const staticItemIds = new Set(staticItems.map((item) => parseInt(String(item.itemId), 10)));
        for (const contact of favoriteContacts ?? []) {
            if (staticItemIds.has(parseInt(String(contact.itemId), 10))) {
                continue;
            }
            items.push(hasGroup ? { ...contact, grp: "Starred" } : { ...contact });
        }

        return this.withSelectedContactInItems(items);
    }

    private withSelectedContactInItems(items: DropDownItem[]): DropDownItem[] {
        const selected = this.value();
        if (!selected?.itemId) {
            return items;
        }
        const selectedId = S25Util.parseInt(selected.itemId);
        if (!selectedId) {
            return items;
        }
        const alreadyListed = items.some((item) => S25Util.parseInt(item.itemId) === selectedId);
        if (alreadyListed) {
            return items;
        }
        return [
            {
                ...selected,
                itemTypeId: selected.itemTypeId ?? Item.Ids.Contact,
                itemName: selected.itemName ?? selected.txt,
                txt: selected.txt ?? selected.itemName,
            },
            ...items,
        ];
    }

    private applyProgrammaticSelectionDisplay(): void {
        const selected = this.value();
        if (!selected?.itemId || !selected.itemName) {
            return;
        }
        const paginated = this.dropdown()?.dropdown?.() as S25DropdownPaginatedComponent | undefined;
        if (!paginated?.model) {
            return;
        }
        paginated.setName(selected);
        paginated.addChosen();
        paginated.refreshChosenSelection();
    }

    onValueChange(data?: DropDownItem | null) {
        this.value.set(data ?? null);
        this.contactSelected.emit(data ?? null);
    }

    focusDropdown() {
        this.dropdown()?.focusDropdown();
    }
}
